/**
 * SettingsAgentEmailPage — Landlord Portal
 *
 * Route: /settings/agent-email (renders inside SettingsLayout; redirects to /recipients)
 *
 * How the landlord's AI agents email people on their behalf, moved under Settings (BR-128):
 * Recipients (who agents can reach), Budgets (what they can send) and Held messages (what is
 * held for the landlord). Each tab is a route that renders the existing relay page in the Outlet.
 */

import { Link, Outlet, useMatch } from 'react-router-dom';
import { PageHeader } from '../components/layout/index';

interface AgentEmailTab {
  label: string;
  /** Shorter label shown at phone width. */
  shortLabel?: string;
  path: string;
  testId: string;
}

const TABS: AgentEmailTab[] = [
  { label: 'Recipients', path: '/settings/agent-email/recipients', testId: 'agent-email-tab-recipients' },
  { label: 'Budgets', path: '/settings/agent-email/budgets', testId: 'agent-email-tab-budgets' },
  {
    label: 'Held messages',
    shortLabel: 'Held',
    path: '/settings/agent-email/policy-blocks',
    testId: 'agent-email-tab-policy-blocks',
  },
];

function AgentEmailTabLink({ tab }: { tab: AgentEmailTab }): JSX.Element {
  const isActive = useMatch({ path: tab.path, end: false }) !== null;
  return (
    <Link
      to={tab.path}
      aria-current={isActive ? 'page' : undefined}
      aria-label={tab.label}
      data-testid={tab.testId}
      className={`inline-flex items-center px-3 md:px-4 min-h-[44px] text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
        isActive
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {tab.shortLabel ? (
        <>
          <span className="md:hidden" aria-hidden="true">{tab.shortLabel}</span>
          <span className="hidden md:inline" aria-hidden="true">{tab.label}</span>
        </>
      ) : (
        tab.label
      )}
    </Link>
  );
}

export function SettingsAgentEmailPage(): JSX.Element {
  return (
    <div className="px-[var(--page-padding-x)]" data-testid="settings-agent-email-page">
      <PageHeader
        title="Agent email"
        subtitle="How your AI agents email people on your behalf: who they can reach, what they can send, and what is held for you."
        subtitleWrap
      />

      <nav aria-label="Agent email" className="flex gap-1 mb-4 border-b border-border">
        {TABS.map((tab) => (
          <AgentEmailTabLink key={tab.path} tab={tab} />
        ))}
      </nav>

      <Outlet />
    </div>
  );
}

export default SettingsAgentEmailPage;
